import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { doc, getDocFromServer, onSnapshot } from "firebase/firestore";
import {
  describeRuntimeSessionRecord,
  readRuntimeTestConfig,
  recordRuntimeDiagnostic,
  runRuntimeTestHook,
} from "../utils/runtimeDiagnostics";
import {
  createPostCompletionReconciliationController,
  isCanonicalPostSessionStateVerified,
} from "../utils/postCompletionReconciliation";
import { getTrustedActiveSummaryState } from "../utils/postSessionProvenance";

export function normalizeLiveSessionScope({
  authScopeKey = "",
  orgId = "",
  sessionId = "",
} = {}) {
  return {
    authScopeKey: String(authScopeKey || "").trim(),
    orgId: String(orgId || "").trim(),
    sessionId: String(sessionId || "").trim(),
  };
}

export function isSelectedSessionRecord(record, sessionId) {
  const selectedSessionId = String(sessionId || "").trim();
  return Boolean(
    record &&
    selectedSessionId &&
    String(record.id || "").trim() === selectedSessionId
  );
}

export function selectSessionRecordForView({
  liveRecord = null,
  fallbackRecord = null,
  sessionId = "",
} = {}) {
  return isSelectedSessionRecord(liveRecord, sessionId)
    ? liveRecord
    : fallbackRecord;
}

export function hasMatchingAuthoritativeCompletion(
  record,
  completionVersionId = ""
) {
  const expectedVersionId = String(completionVersionId || "").trim();
  return Boolean(
    expectedVersionId &&
    record?.sessionComplete === true &&
    String(record?.completionVersion?.id || "").trim() === expectedVersionId
  );
}

export function sessionRecordFromSnapshot(snapshot) {
  if (!snapshot?.exists?.()) return null;
  return {
    ...(snapshot.data?.() || {}),
    // The Firestore document ID is authoritative even if a stale cached `id`
    // field exists inside the document.
    id: String(snapshot.id || ""),
  };
}

function timestampMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return Number(value.toMillis()) || 0;
  if (typeof value?.toDate === "function") return value.toDate().getTime() || 0;
  if (typeof value?.seconds === "number") {
    return Number(value.seconds) * 1000 + Number(value.nanoseconds || 0) / 1e6;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

function statusToken(value = "") {
  return String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
}

const SUMMARY_STATUS_RANK = Object.freeze({
  "": 0,
  draft: 1,
  pending: 1,
  generating: 1,
  final: 2,
  reviewed: 2,
  failed: 2,
  stale: 2,
  superseded: 2,
});
const FINALIZATION_STATUS_RANK = Object.freeze({
  "": 0,
  not_started: 0,
  pending: 1,
  succeeded: 2,
  reused: 2,
  failed: 2,
});
const EMAIL_STATUS_RANK = Object.freeze({
  "": 0,
  not_started: 0,
  not_requested: 0,
  pending: 1,
  requested: 1,
  claiming: 1,
  dispatching: 1,
  queued: 2,
  queue_accepted: 2,
  sent: 3,
  accepted: 3,
  provider_accepted: 3,
  delivered: 3,
  failed: 3,
  ambiguous: 3,
  bounced: 3,
  suppressed: 3,
});
const REMINDER_STATUS_RANK = Object.freeze({
  "": 0,
  not_started: 0,
  not_scheduled: 0,
  scheduled: 1,
  pending: 1,
  dispatching: 1,
  queued: 2,
  queue_accepted: 2,
  sent: 2,
  provider_accepted: 2,
  delivered: 2,
  failed: 2,
  ambiguous: 2,
  cancelled: 2,
  canceled: 2,
  suppressed: 2,
});

function statusRank(value, ranks) {
  return ranks[statusToken(value)] ?? 0;
}

function preserveAdvancedState(previous, incoming, ranks, preferPrevious = false) {
  if (!previous || typeof previous !== "object") return incoming;
  if (!incoming || typeof incoming !== "object") return previous;
  const previousRank = statusRank(previous.status, ranks);
  const incomingRank = statusRank(incoming.status, ranks);
  return previousRank > incomingRank || (preferPrevious && previousRank >= incomingRank)
    ? previous
    : incoming;
}

function completionVersionId(record = {}) {
  return String(record?.completionVersion?.id || "").trim();
}

function summaryStatus(summary = {}) {
  return summary?.generationStatus || summary?.status || "";
}

function sameCompletedTranscript(previous = {}, incoming = {}) {
  const previousVersion = previous?.completionVersion || {};
  const incomingVersion = incoming?.completionVersion || {};
  return Boolean(
    previousVersion.id &&
    previousVersion.id === incomingVersion.id &&
    String(previousVersion.transcriptFingerprint || "") ===
      String(incomingVersion.transcriptFingerprint || "") &&
    Number(previousVersion.sourceMessageCount) ===
      Number(incomingVersion.sourceMessageCount)
  );
}

function activeAcceptedBoundary(record = {}) {
  const accepted = record?.postSessionContinuation?.activeSummary?.accepted || {};
  return {
    sourceMessageCount: Number(accepted.sourceMessageCount) || 0,
    acceptedAt: timestampMillis(accepted.acceptedAt),
  };
}

function recordTranscriptCount(record = {}) {
  return Array.isArray(record.messages)
    ? record.messages.length
    : Number(record.messageCount) || 0;
}

function hasSameSessionIdentity(previous = {}, incoming = {}) {
  return Boolean(
    String(previous.id || "").trim() &&
    String(previous.id || "").trim() === String(incoming.id || "").trim() &&
    String(previous.orgId || "").trim() === String(incoming.orgId || "").trim() &&
    String(previous.userId || "").trim() === String(incoming.userId || "").trim()
  );
}

function establishesTrustedCurrentActiveSummary(previous = {}, incoming = {}) {
  const incomingState = getTrustedActiveSummaryState(incoming);
  if (
    incomingState.snapshotValid !== true ||
    incomingState.state !== "current" ||
    incomingState.exact !== true
  ) {
    return false;
  }
  if (recordTranscriptCount(incoming) < recordTranscriptCount(previous)) {
    return false;
  }
  const previousState = getTrustedActiveSummaryState(previous);
  if (
    previousState.snapshotValid !== true ||
    previousState.state !== "current" ||
    previousState.exact !== true
  ) {
    return true;
  }
  const previousBoundary = activeAcceptedBoundary(previous);
  const incomingBoundary = activeAcceptedBoundary(incoming);
  return Boolean(
    incomingBoundary.sourceMessageCount > previousBoundary.sourceMessageCount ||
    (
      incomingBoundary.sourceMessageCount === previousBoundary.sourceMessageCount &&
      incomingBoundary.acceptedAt >= previousBoundary.acceptedAt
    )
  );
}

export function mergeMonotonicSessionRecord(previous = null, incoming = null) {
  if (!previous) return incoming;
  if (!incoming) return previous;
  if (!hasSameSessionIdentity(previous, incoming)) return previous;
  // Listener/server-read values are full session documents. The client starts a
  // new document for resets, so a shorter transcript on this exact identity is
  // stale evidence even when its root updatedAt is newer.
  if (recordTranscriptCount(incoming) < recordTranscriptCount(previous)) {
    return previous;
  }

  const previousCompletionId = completionVersionId(previous);
  const incomingCompletionId = completionVersionId(incoming);
  if (previousCompletionId && incomingCompletionId !== previousCompletionId) {
    return previous;
  }

  const previousContinuation = previous.postSessionContinuation || {};
  const incomingContinuation = incoming.postSessionContinuation || {};
  const previousSummary = previous.sessionSummary || null;
  const incomingSummary = incoming.sessionSummary || null;
  const previousUpdatedAt = timestampMillis(previous.updatedAt);
  const incomingUpdatedAt = timestampMillis(incoming.updatedAt);
  const incomingIsOlder = previousUpdatedAt > incomingUpdatedAt;
  const previousIsAtLeastAsNew = previousUpdatedAt >= incomingUpdatedAt;
  const incomingEstablishesCanonicalTrust = Boolean(
    isCanonicalPostSessionStateVerified(incoming) &&
    !isCanonicalPostSessionStateVerified(previous)
  );
  const incomingEstablishesActiveTrust =
    establishesTrustedCurrentActiveSummary(previous, incoming);
  const incomingEstablishesProtectedSummaryTrust = Boolean(
    incomingEstablishesCanonicalTrust || incomingEstablishesActiveTrust
  );
  const previousActiveSummaryState = getTrustedActiveSummaryState(previous);
  const preservePreviousActiveTrust = Boolean(
    incoming.sessionComplete !== true &&
    previousActiveSummaryState.snapshotValid === true &&
    previousActiveSummaryState.state === "current" &&
    previousActiveSummaryState.exact === true &&
    !incomingEstablishesActiveTrust &&
    recordTranscriptCount(incoming) <= recordTranscriptCount(previous)
  );
  const previousSummaryRank = statusRank(
    summaryStatus(previousSummary),
    SUMMARY_STATUS_RANK
  );
  const incomingSummaryRank = statusRank(
    summaryStatus(incomingSummary),
    SUMMARY_STATUS_RANK
  );
  const keepPreviousSummary = Boolean(
    previousSummary &&
    (
      !incomingSummary ||
      previousSummaryRank > incomingSummaryRank ||
      (
        previousSummaryRank >= incomingSummaryRank &&
        !incomingEstablishesProtectedSummaryTrust &&
        previousIsAtLeastAsNew &&
        String(previousSummary.summaryVersion || "") &&
        String(incomingSummary.summaryVersion || "") &&
        String(previousSummary.summaryVersion) !==
          String(incomingSummary.summaryVersion)
      ) ||
      (
        previousSummaryRank >= incomingSummaryRank &&
        !incomingEstablishesProtectedSummaryTrust &&
        previousIsAtLeastAsNew &&
        String(previousSummary.sourceTranscriptFingerprint || "") &&
        String(incomingSummary.sourceTranscriptFingerprint || "") &&
        String(previousSummary.sourceTranscriptFingerprint) !==
          String(incomingSummary.sourceTranscriptFingerprint)
      )
    )
  );
  const preserveCompletedTranscript = Boolean(
    previousCompletionId &&
    (
      !incomingCompletionId ||
      !sameCompletedTranscript(previous, incoming)
    )
  );
  const baseRecord = incomingIsOlder ? previous : incoming;
  const baseContinuation = incomingIsOlder
    ? previousContinuation
    : incomingContinuation;

  return {
    ...baseRecord,
    sessionComplete:
      previous.sessionComplete === true ? true : incoming.sessionComplete,
    endedAt:
      incomingIsOlder || (previousCompletionId && !incomingCompletionId)
        ? previous.endedAt
        : incoming.endedAt,
    completionReason:
      incomingIsOlder || (previousCompletionId && !incomingCompletionId)
        ? previous.completionReason
        : incoming.completionReason,
    completionAction:
      incomingIsOlder || (previousCompletionId && !incomingCompletionId)
        ? previous.completionAction
        : incoming.completionAction,
    completionVersion:
      incomingIsOlder || (previousCompletionId && !incomingCompletionId)
        ? previous.completionVersion
        : incoming.completionVersion,
    messages: incomingEstablishesActiveTrust
      ? incoming.messages
      : incomingIsOlder || preserveCompletedTranscript
        ? previous.messages
        : incoming.messages,
    messageCount: incomingEstablishesActiveTrust
      ? incoming.messageCount
      : incomingIsOlder || preserveCompletedTranscript
        ? previous.messageCount
        : incoming.messageCount,
    sessionSummary: incomingEstablishesActiveTrust
      ? incomingSummary
      : preservePreviousActiveTrust || keepPreviousSummary ||
        (
          incomingIsOlder &&
          !incomingEstablishesProtectedSummaryTrust &&
          previousSummary &&
          previousSummaryRank >= incomingSummaryRank
        )
          ? previousSummary
          : incomingSummary,
    postSessionContinuation: {
      ...baseContinuation,
      activeSummary: incoming.sessionComplete === true
        ? incomingContinuation.activeSummary
        : incomingEstablishesActiveTrust
          ? incomingContinuation.activeSummary
          : preservePreviousActiveTrust
            ? previousContinuation.activeSummary
            : incomingContinuation.activeSummary || previousContinuation.activeSummary,
      completion:
        previousContinuation.completion && !incomingContinuation.completion
          ? previousContinuation.completion
          : incomingContinuation.completion,
      finalization: preserveAdvancedState(
        previousContinuation.finalization,
        incomingContinuation.finalization,
        FINALIZATION_STATUS_RANK,
        incomingIsOlder && !incomingEstablishesCanonicalTrust
      ),
      automaticEmail: preserveAdvancedState(
        previousContinuation.automaticEmail,
        incomingContinuation.automaticEmail,
        EMAIL_STATUS_RANK,
        incomingIsOlder && !incomingEstablishesCanonicalTrust
      ),
      manualResend:
        incomingIsOlder
          ? previousContinuation.manualResend || incomingContinuation.manualResend
          : incomingContinuation.manualResend || previousContinuation.manualResend,
      reminder: preserveAdvancedState(
        previousContinuation.reminder,
        incomingContinuation.reminder,
        REMINDER_STATUS_RANK,
        incomingIsOlder && !incomingEstablishesCanonicalTrust
      ),
      return: incomingIsOlder
        ? previousContinuation.return || incomingContinuation.return
        : incomingContinuation.return || previousContinuation.return,
    },
    updatedAt:
      previousUpdatedAt > incomingUpdatedAt ? previous.updatedAt : incoming.updatedAt,
  };
}

export default function useLiveSessionRecord({
  db,
  authScopeKey = "",
  orgId = "",
  sessionId = "",
  enabled = true,
  sessionOrigin = "none",
  persistenceState = "listener_detached",
  listenerAttempt = 0,
  onListenerEvent = null,
  reconcileAfterCompletion = false,
  reconciliationSchedule = undefined,
} = {}) {
  const scope = useMemo(
    () => normalizeLiveSessionScope({ authScopeKey, orgId, sessionId }),
    [authScopeKey, orgId, sessionId]
  );
  const scopeKey = `${scope.authScopeKey}:${scope.orgId}:${scope.sessionId}`;
  const [snapshotState, setSnapshotState] = useState(null);
  const snapshotStateRef = useRef(snapshotState);
  snapshotStateRef.current = snapshotState;
  const reconciliationControllerRef = useRef(null);
  const listenerContextRef = useRef({});
  listenerContextRef.current = {
    sessionOrigin,
    persistenceState,
    listenerAttempt,
  };
  const onListenerEventRef = useRef(onListenerEvent);
  onListenerEventRef.current = onListenerEvent;
  const applyIncomingRecord = useCallback((record, context = {}) => {
    let appliedRecord = record;
    setSnapshotState((previous) => {
      const nextRecord = previous?.scopeKey === scopeKey
        ? mergeMonotonicSessionRecord(previous.record, record)
        : record;
      appliedRecord = nextRecord;
      recordRuntimeDiagnostic(
        context.source === "server_reconciliation"
          ? "post-completion-reconciliation.state-set"
          : "live-session.state-set",
        {
          snapshotCount: context.snapshotCount || 0,
          attempt: context.attempt || 0,
          record: describeRuntimeSessionRecord(nextRecord),
          incomingRecord: describeRuntimeSessionRecord(record),
          preservedPreviousRecord: nextRecord === previous?.record,
        }
      );
      return { scopeKey, record: nextRecord, error: null };
    });
    return appliedRecord;
  }, [scopeKey]);

  useEffect(() => {
    if (!enabled || !db || !scope.orgId || !scope.sessionId) {
      recordRuntimeDiagnostic("live-session.listener-skipped", {
        enabled,
        hasDb: Boolean(db),
        ...listenerContextRef.current,
        selectedSessionId: scope.sessionId,
        selectedSessionIdType: typeof scope.sessionId,
      });
      return undefined;
    }

    const ref = doc(db, "orgs", scope.orgId, "sessions", scope.sessionId);
    const documentPath = `orgs/${scope.orgId}/sessions/${scope.sessionId}`;
    let snapshotCount = 0;
    const attachedAt = new Date().toISOString();
    recordRuntimeDiagnostic("live-session.listener-attached", {
      documentPath,
      attachedAt,
      ...listenerContextRef.current,
      selectedSessionId: scope.sessionId,
      selectedSessionIdType: typeof scope.sessionId,
    });
    const unsubscribe = onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snapshot) => {
        snapshotCount += 1;
        const record = sessionRecordFromSnapshot(snapshot);
        const documentUpdateTime =
          snapshot?.updateTime?.toMillis?.() ||
          snapshot?._document?.version?.timestamp?.toMillis?.() ||
          null;
        recordRuntimeDiagnostic("live-session.snapshot", {
          documentPath,
          snapshotCount,
          exists: Boolean(snapshot?.exists?.()),
          fromCache: Boolean(snapshot?.metadata?.fromCache),
          hasPendingWrites: Boolean(snapshot?.metadata?.hasPendingWrites),
          documentUpdateTime,
          record: describeRuntimeSessionRecord(record),
        });
        if (!record) return;
        if (
          record.sessionComplete === true &&
          readRuntimeTestConfig("suppressCompletedListenerSnapshots") === true
        ) {
          recordRuntimeDiagnostic("live-session.snapshot-suppressed-for-test", {
            documentPath,
            snapshotCount,
            record: describeRuntimeSessionRecord(record),
          });
          return;
        }
        applyIncomingRecord(record, { source: "listener", snapshotCount });
        reconciliationControllerRef.current?.observeRecord(record, "listener");
      },
      (error) => {
        recordRuntimeDiagnostic("live-session.listener-error", {
          documentPath,
          code: String(error?.code || ""),
        });
        setSnapshotState((previous) => ({
          scopeKey,
          record: previous?.scopeKey === scopeKey ? previous.record : null,
          error,
        }));
        onListenerEventRef.current?.({
          type: "listener_failed",
          orgId: scope.orgId,
          sessionId: scope.sessionId,
          at: new Date().toISOString(),
          errorCode: String(error?.code || ""),
        });
      }
    );
    onListenerEventRef.current?.({
      type: "listener_attached",
      orgId: scope.orgId,
      sessionId: scope.sessionId,
      at: attachedAt,
    });
    return () => {
      const detachedAt = new Date().toISOString();
      recordRuntimeDiagnostic("live-session.listener-detached", {
        documentPath,
        attachedAt,
        detachedAt,
        snapshotCount,
      });
      unsubscribe();
      onListenerEventRef.current?.({
        type: "listener_detached",
        orgId: scope.orgId,
        sessionId: scope.sessionId,
        at: detachedAt,
      });
    };
  }, [
    db,
    enabled,
    applyIncomingRecord,
    listenerAttempt,
    scope.authScopeKey,
    scope.orgId,
    scope.sessionId,
    scopeKey,
  ]);

  useEffect(() => {
    if (
      !reconcileAfterCompletion ||
      !enabled ||
      !db ||
      !scope.authScopeKey ||
      !scope.orgId ||
      !scope.sessionId
    ) {
      return undefined;
    }

    const ref = doc(db, "orgs", scope.orgId, "sessions", scope.sessionId);
    const documentPath = `orgs/${scope.orgId}/sessions/${scope.sessionId}`;
    const runtimeSchedule = readRuntimeTestConfig(
      "postCompletionReconciliationDelaysMs"
    );
    const controller = createPostCompletionReconciliationController({
      schedule: reconciliationSchedule ?? runtimeSchedule,
      getCurrentRecord: () => (
        snapshotStateRef.current?.scopeKey === scopeKey
          ? snapshotStateRef.current.record
          : null
      ),
      readRecord: async (_readScope, attemptContext) => {
        await runRuntimeTestHook("beforePostCompletionReconciliationRead", {
          documentPath,
          attempt: attemptContext.attempt,
          maxAttempts: attemptContext.maxAttempts,
        });
        const snapshot = await getDocFromServer(ref);
        const record = sessionRecordFromSnapshot(snapshot);
        await runRuntimeTestHook("beforePostCompletionReconciliationApply", {
          documentPath,
          attempt: attemptContext.attempt,
          record: describeRuntimeSessionRecord(record),
        });
        return record;
      },
      applyRecord: (record, context) => applyIncomingRecord(record, context),
      onEvent: (type, detail) => {
        recordRuntimeDiagnostic(`post-completion-reconciliation.${type}`, {
          ...detail,
          ...listenerContextRef.current,
          selectedSessionId: scope.sessionId,
          selectedSessionIdType: typeof scope.sessionId,
        });
      },
    });
    reconciliationControllerRef.current = controller;
    controller.start(scope);

    return () => {
      controller.cancel("scope_changed_or_component_unmounted");
      if (reconciliationControllerRef.current === controller) {
        reconciliationControllerRef.current = null;
      }
    };
  }, [
    applyIncomingRecord,
    db,
    enabled,
    reconcileAfterCompletion,
    reconciliationSchedule,
    scope,
    scope.authScopeKey,
    scope.orgId,
    scope.sessionId,
    scopeKey,
  ]);

  if (snapshotState?.scopeKey !== scopeKey) {
    return { record: null, error: null };
  }
  return {
    record: snapshotState.record || null,
    error: snapshotState.error || null,
  };
}
